import {animate, style, transition, trigger} from '@angular/animations';
import {ApplicationRef, Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {SwUpdate} from '@angular/service-worker';
import {TranslateService} from '@ngx-translate/core';
import {loadMessages, locale} from 'devextreme/localization';
import * as deMessages from 'devextreme/localization/messages/de.json';
import * as enMessages from 'devextreme/localization/messages/en.json';
import * as esMessages from 'devextreme/localization/messages/es.json';
import * as frMessages from 'devextreme/localization/messages/fr.json';
import * as itMessages from 'devextreme/localization/messages/it.json';
import * as ptMessages from 'devextreme/localization/messages/pt.json';
import * as caMessages from './devextreme-files/ca.json';
import {
  combineLatest,
  filter,
  firstValueFrom,
  forkJoin,
  interval,
  map,
  Observable,
  pairwise,
  skip,
  startWith,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import {environment} from 'src/environments/environment';
import {TabRole, TabsService} from './libs/components/tabs-host/tabs.service';
import {
  Alert,
  AppUser,
  CustomReport,
  EAppointmentsFilterStates,
  EReportType,
  EActivableStateType,
  IProductLabelSettings,
  Printer,
  ProductLabelSizes,
  ProductLabelTypes,
  TId,
  TimeTicket,
  User, CustomReportResolveResponse,
  DocumentType
} from './models';
import { AdminTenantsComponent } from './libs/components/admin/admin-tenants/admin-tenants.component';
import {
  OrganizationPersonsAddComponent
} from './routes/directory/directory-stakeholders/organizations/organization-persons/organization-persons-add/organization-persons-add.component';
import {
  OrganizationsAddComponent
} from './routes/directory/directory-stakeholders/organizations/organizations-add/organizations-add.component';
import {
  ProductsAddComponent
} from './routes/products/operative/products-operative-products/products-add/products-add.component';
import { UserProfileComponent } from './routes/user/user-profile/user-profile.component';
import { AlertService } from './services/alert.service';
import { AuthService } from './services/auth.service';
import { BreadcrumbService } from './services/breadcrumb.service';
import { ConfigService, ENavBarOptions, SlideMenuOption } from './services/config.service';
import { LangService } from './services/lang.service';
import { MasterApiService } from './services/master-api.service';
import { PrintersService } from './services/printers.service';
import { ReportViewerService } from './services/report-viewer.service';
import { ITranslatableField, TranslationsService } from './services/translations.service';
import { UserFavoriteMenuOptionsService } from './services/user-favorite-menu-options.service';
import { AdminStockClosuresComponent } from './libs/components/admin/admin-stock-closures/admin-stock-closures.component';
import { GenericMultieditorComponent } from './libs/components/generic-crud/generic-editor/generic-multieditor.component';
import { HttpTransportType, HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { PmsProjectsAddComponent } from './routes/pms/operative/pms-projects-add/pms-projects-add.component';
import { AclService, EAccessName } from './libs/services/acl.service';
import { applyBryntumLocale } from './libs/utils/bryntum.helper';
import {
  AdminAppAndroidVersionComponent
} from './libs/components/admin/admin-app-android-version/admin-app-android-version.component';
import { StorageService } from './services/storage.service';


// TODO
// https://jasonwatmore.com/post/2020/07/25/angular-10-jwt-authentication-with-refresh-tokens

@Component({
    animations: [
        trigger('slide', [
            transition(':enter', [
                style({ transform: 'translateY(-100%)' }),
                animate('0.5s ease-in-out', style({ transform: 'translateY(0%)' }))
            ]),
            transition(':leave', [
                style({ transform: 'translateY(0%)' }),
                animate('0.5s ease-in-out', style({ transform: 'translateY(-100%)' }))
            ])
        ])
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.sass'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  ready$: Observable<boolean> = combineLatest([this.auth.ready$]).pipe(map(([auth]) => auth));
  user$: Observable<AppUser | null> = this.auth.user$;
  logout$: Subject<void>;
  isProduction = environment.production;
  translationField$: Subject<ITranslatableField<any> | undefined>;

  activeTimeTikets: TimeTicket[];
  companies$ = this.auth.companies$;
  langCode$ = this.lang.code$;
  breadcrumb$ = this.breadcrumb.menuItems$;
  navTheme$ = this.breadcrumb.navTheme$;

  _isDrawerOpen = false;
  messages: { [key: string]: any } = { ...enMessages, ...esMessages, ...caMessages, ...deMessages, ...itMessages, ...ptMessages, ...frMessages };

  tabs = this.tabsSrv.tabs;

  set isDrawerOpen(value: boolean) {
    this._isDrawerOpen = value;
  }

  get isDrawerOpen(): boolean {
    return this._isDrawerOpen;
  }


  resetPassword: boolean = false;
  resetPasswordToken: string = '';
  resetPasswordEmail: string = '';
  resetPasswordUserName: string = '';
  resetPasswordOldPassword: string = '';

  destroy$: Subject<void>;
  isCreatingPerson: boolean = false;

  breadcrumbLogo = environment.breadcrumbLogo + '?rnd=' + new Date().getTime();

  //favoriteMenuOptions$: Observable<SlideMenuOption> = [this.userFavoriteMenuOptionsService.fav$];
  favoriteMenuOptions$ = this.userFavoriteMenuOptionsService.favOptions$;

  readonly selectCompanyClick = async (e: any): Promise<void> => await this.selectCompany(e.itemData.dataField);

  readonly reportViewer = this.reportViewerSrv;
  readonly printers$: Observable<Printer[]> = this.printersSrv.printers$;
  isSelectingPrinter = false;
  selectedPrinter$ = new Subject<TId>();

  readonly customReports$: Observable<CustomReportResolveResponse[]> = this.printersSrv.customReports$;
  isSelectingCustomReport = false;
  selectedCustomReports$ = new Subject<TId>();

  productLabelSizes = ProductLabelSizes;
  productLabelTypes: { id: number, name: string }[] = [];
  productCustomReports: CustomReport[] = [];
  isSelectingPrinterSettingsForProducts = false;
  defaultPrinterSettingsForProducts?: IProductLabelSettings;
  selectedPrinterSettingsForProducts$ = new Subject<IProductLabelSettings>();
  cancelSelectedPrinterSettingsForProducts$ = new Subject<void>();

  acl$ = this.acl.acl$;

  // NavBarItemsVisibility
  globalSearchVisible = true;
  homeVisible = true;
  fastReleaseVisible = true;
  favoriteOptionsVisible = true;
  plannerVisible = true;
  calendarVisible = true;
  timesheetVisible = true;
  currentTimeVisible = true;
  appointmentsVisible = true;
  companiesVisible = true;
  languagesVisible = true;
  currenciesVisible = true;
  countriesVisible = true;
  tagsVisible = true;
  alertsVisible = true;
  eventsVisible = true;
  translationsVisible = true;
  customFieldsOrganizationsVisible = true;
  customFieldsPeopleVisible = true;
  customFieldsSuppliersVisible = true;
  customFieldsProductsVisible = true;
  customFieldsBatchesVisible = true;
  reportsVisible = true;
  printersVisible = true;
  scalesVisible = true;
  devicesVisible = true;
  paperSizeVisible = true;
  notificationsVisible = true;
  externalServiceNotificationsVisible = true;
  tenantsVisible = true;
  stockClosures = true;
  rolesVisible = true;
  usersVisible = true;
  documentTypesVisible = true;
  emailTemplatesVisible = true;
  pritersPopupShowUnits = false;
  numberOfCopies:string = '1';
  canSeeoperationPlanning = environment.features.enableScheduler
  clockingTerminalVisible = true;

  documentTypes:DocumentType[] = [];

  constructor(private auth: AuthService,
    private alert: AlertService,
    private lang: LangService,
    private swUpdate: SwUpdate,
    private api: MasterApiService,
    private tabsSrv: TabsService,
    private breadcrumb: BreadcrumbService,
    private router: Router,
    private acl: AclService,
    private translate: TranslateService,
    private translateGui: TranslationsService,
    private reportViewerSrv: ReportViewerService,
    private app: ApplicationRef,
    private printersSrv: PrintersService,
    private route: ActivatedRoute,
    private userFavoriteMenuOptionsService: UserFavoriteMenuOptionsService,
    private configService: ConfigService,
    private storage: StorageService) {
    loadMessages(this.messages);
    this.selectCompany = this.selectCompany.bind(this);
    const storedLang = localStorage.getItem('lang');
    // Manage the translatable editor popup
    this.translationField$ = this.translateGui.translateField$;
    this.translationField$.pipe(
      filter(x => x !== undefined),
    ).subscribe(async x => {
      await firstValueFrom(x!.callback$.asObservable());
      this.translationField$.next(undefined);
    });
    this.printersSrv.printerChooser = async () => {
      this.isSelectingPrinter = true;
      if (this.printersSrv.showUnits) {
        this.pritersPopupShowUnits = true;
      }
      const selected = await firstValueFrom(this.selectedPrinter$);
      this.printersSrv.units = Number(this.numberOfCopies);
      this.isSelectingPrinter = false;
      return selected;
    };
    this.printersSrv.customReportChooser = async () => {
      this.isSelectingCustomReport = true;
      const selected = await firstValueFrom(this.selectedCustomReports$);
      const customReports = await firstValueFrom(this.customReports$)
      const selectedCustomReport = customReports.find(c => c.id === selected)
      this.printersSrv.units = Number(this.numberOfCopies);
      this.isSelectingCustomReport = false;
      return selectedCustomReport!;
    };
    this.printersSrv.printerChooserForProduct = async (defaultSettings?: IProductLabelSettings) => {
      this.numberOfCopies = '1';
      this.productLabelTypes = ProductLabelTypes.map(_ => ({ id: _.id, name: this.translate.instant(`enumeration.ProductLabelTypes.${_.name}`) }));

      this.productCustomReports = await firstValueFrom(this.api.resources.reporting.customReports.getAll({ reportTypeId: EReportType.ProductLabel, stateId: EActivableStateType.Active }));

      this.defaultPrinterSettingsForProducts = defaultSettings;
      if (this.printersSrv.showUnits) {
        this.pritersPopupShowUnits = true;
      }
      this.isSelectingPrinterSettingsForProducts = true;
      const selected = await firstValueFrom(this.selectedPrinterSettingsForProducts$.pipe(
        takeUntil(this.cancelSelectedPrinterSettingsForProducts$)
      ));
      this.printersSrv.units = Number(this.numberOfCopies);
      this.isSelectingPrinterSettingsForProducts = false;
      return selected;
    }
    this.lang.code$.pipe(
      takeUntil(this.swUpdate.versionUpdates),
      startWith(storedLang ?? environment.defaultLanguage),
      pairwise(),
      filter(([prev, curr]) => {
        return prev !== curr || this.translate.currentLang === undefined
      }),
      map(([_prev, curr]) => curr),
    ).subscribe(code => {
      localStorage.setItem('lang', code);
      this.translate.use(code);
      locale(code);  // TODO: Comprovar per que devextrem no tradueix els missatges al vol.
      applyBryntumLocale(code);
    });
    this.activeTimeTikets = [];

    if (auth?.userValue?.languageCode !== undefined) {
      this.lang.code = auth!.userValue!.languageCode;
    }
    else if (storedLang !== null) {
      this.lang.code = storedLang;
    }

    if (auth?.userValue !== null && auth?.userValue !== undefined) {
      // Recarregar la pàgina (F5) no dispara la transició null->user del subscribe de user$ de sota,
      // ja que l'usuari ja ve carregat des de l'APP_INITIALIZER (auth.init()).
      this.userFavoriteMenuOptionsService.initializeUserFavoriteOptions();
    }


    const interval$ = interval(60 * 1000).pipe(startWith(0));

    if (this.swUpdate.isEnabled) {
      interval$.subscribe(async () => {
        try {
          const updateFound = await this.swUpdate.checkForUpdate();
          if (updateFound) {
            this.alert.info(this.translate.instant('common.newVersion'));
            await this.swUpdate.activateUpdate();
            document.location.reload();
          }
        } catch (e) {
          console.error('Error checking for update', e);
        }
      });
    }

    this.logout$ = new Subject<void>();
    this.user$.pipe(
      skip(1),
      pairwise(),
      filter(([prev]) => prev === null),
    ).subscribe(([, user]) => {
      if (user !== null) { // Login d'usuari
        this.onLogin(user);
        this.userFavoriteMenuOptionsService.initializeUserFavoriteOptions();
      } else { // Logout d'usuari
        this.onLogout();
      }

    });

    this.user$.subscribe(() => {
      this.setupNavBarItemsVisibility();
    });

    this.destroy$ = new Subject();
    this.acl$.subscribe(x => console.log(x));
  }

  NumberOfCopiesIsValidNumber = (): boolean => {
    const number = Number(this.numberOfCopies);
    const isDecimal = this.numberOfCopies.includes('.') || this.numberOfCopies.includes(',');
    return !isNaN(number) && number > 0 && !isDecimal;
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(x => {
      if (x.has('token')) {
        this.resetPasswordToken = x.get('token') ?? '';
        this.resetPasswordEmail = x.get('Email') ?? '';
        this.resetPassword = true;
      } else {
        this.resetPassword = false;
      }
    });

    if (this.auth.userValue) this.onLogin(this.auth.userValue);
    this.acl$.subscribe(x => console.log(x));
    if (this.auth.userValue) this.storage.getCompanyCurrency()

    this.wsConnect();

    this.auth.user$.subscribe(user => {
      this.clockingTerminalVisible = user !== null && user !== undefined && user.workerId !== null && user.workerId !== undefined;
    });
  }

  ngOnDestroy(): void {
    this.wsDisconnect();
  }

  userHasAccessToHisLastCompany = (): boolean => {
    const companies = this.auth.companiesValue
    const lastCompanyId = this.auth.userValue?.lastCompanyId;

    return companies.some(_ => _.id === lastCompanyId);
  }

  async login(username: string, password: string): Promise<void> {
    try {
      await this.auth.login(username, password);
    } catch (error) {
      if (error instanceof Error) {
        this.alert.error(error.message);
      }
      else {
        this.alert.error('error');
        throw error;
      }
    }
  }

  async logout(): Promise<void> {
    await this.auth.logout();
  }

  async selectCompany(company: any): Promise<void> {
    await this.auth.setAndUpdateUserCompany(company.id);
  }

  onMenuItemClick(e: any): void {
    if (e.itemData.dataField) {
      if (e.itemData.dataField.f) {
        (this as any)[e.itemData.dataField.f]({});
      } else {
        this.router.navigate([e.itemData.dataField]);
      }
    }
  }

  onUserMenuItemClick(e: any): void {
    if (e.itemData.dataField) {
      if (e.itemData.dataField.f === 'showProfile') {
        this.showProfile(this.auth.userValue!);
      } else if (e.itemData.dataField.f === 'logout') {
        this.logout();
      }
    }
  }

  private async onLogin(user: AppUser): Promise<void> {
    this.resetPassword = user.changePassword;

    if (this.resetPassword) {
      this.resetPasswordUserName = user.userName;
      this.resetPasswordOldPassword = 'no_needed';
    }

    this.lang.code = user.languageCode
    if (environment.production === true && user.lastUrl) {
      this.router.navigate(['/' + user.lastUrl]);
    }

    await this.getActiveAlerts();
    await this.getActiveAppointments();

    if (this.acl.isAllowed(EAccessName.PlanificationOperative)
      || this.acl.isAllowed(EAccessName.ProductionOrdersOperative)) {
      const checkWorkerTimeTikets = this.api.resources.workerTimeTickets.contextualizeQueryParams({ workerId: (this.auth.userValue!.workerId || '-1').toString(), temporary: 'true' }).getAll()
      this.activeTimeTikets = (await firstValueFrom(checkWorkerTimeTikets)).filter(x => x.workerId?.toString() === this.auth.userValue!.workerId!.toString());
    }


    // Fill catcheable currencies data initialation.
    await firstValueFrom(forkJoin([
      this.storage.getCurrencies(),
      this.storage.getLanguages(),
      this.storage.getCompanyCurrency()
    ]));

    this.documentTypes = await firstValueFrom(this.api.resources.operations.documentTypes.getAll());
    this.documentTypes.sort((a, b) => a.name.localeCompare(b.name));
  }

  private async onLogout(): Promise<void> {
    this.router.navigate(['/']);
    this.logout$.next();
  }

  showProfile(user: User) {
    const options = { inputPrms: { entity: user, entityKey: 'userName' } };
    const tab = this.tabsSrv.createTab(UserProfileComponent, TabRole.EDITION, options);
    tab.component.instance.entityChanged.pipe(takeUntil(this.destroy$), take(1)).subscribe(tab.close);
  }

  async showTenant(): Promise<void> {
    const tab = this.tabsSrv?.createTab(GenericMultieditorComponent, TabRole.EDITION, {
      inputPrms: {
        title: this.translate.instant('menu.preferences.adminMenu.tenants'),
        editors: [
          { cmpt: AdminTenantsComponent },
          { cmpt: AdminStockClosuresComponent },
          { cmpt: AdminAppAndroidVersionComponent }
        ],
      },
    });
    tab.component.instance.entityChanged
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe(() => tab.close());
  }

  async createOrganizationClicked(event: any): Promise<void> {
    const countries = await firstValueFrom(this.storage.getCountries());
    const tab = this.tabsSrv?.createTab(OrganizationsAddComponent, TabRole.CREATION, {
      inputPrms: {
        countries: countries
      }
    });
    tab.component.instance.entityCreated.pipe(
      takeUntil(combineLatest([this.destroy$])),
    ).subscribe(x => this.tabsSrv.closeTab(tab));
  }

  createPersonClicked(_event: any) {
    const tab = this.tabsSrv?.createTab(OrganizationPersonsAddComponent, TabRole.CREATION);
    tab.component.instance.entityCreated.pipe(
      takeUntil(combineLatest([this.destroy$])),
    ).subscribe(() => this.tabsSrv.closeTab(tab));
  }

  createProductClicked() {
    const tab = this.tabsSrv?.createTab(ProductsAddComponent, TabRole.CREATION);
    tab.component.instance.entityCreated.pipe(
      takeUntil(combineLatest([this.destroy$]))
    ).subscribe(x => this.tabsSrv.closeTab(tab));
  }

  createProjectClicked() {
    const tab = this.tabsSrv?.createTab(PmsProjectsAddComponent, TabRole.CREATION);
    tab.component.instance.entityCreated.pipe(
      takeUntil(combineLatest([this.destroy$]))
    ).subscribe(() => this.tabsSrv.closeTab(tab));
  }

  selectedReportId$ = this.customReports$.pipe(
    map(reports => reports?.length ? reports[0].id : null)
  );

  favoriteMenuOptionClicked(item: SlideMenuOption) {
    this.router.navigate([item.link], { queryParamsHandling: 'merge' });
    this.userFavoriteMenuOptionsService.travelLink = item.link;
    this.userFavoriteMenuOptionsService.isTravelingViaFavorite = true;
    this.userFavoriteMenuOptionsService.isFirstTravel = true;
    this.router.navigate([item.link]);
  }

  setupNavBarItemsVisibility() {
    this.globalSearchVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_GlobalSearch);
    this.homeVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Home);
    if (!this.auth.userValue?.homeList || this.auth.userValue?.homeList.length === 0) {
      this.homeVisible = false;
    }
    this.fastReleaseVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_FastRelease);
    this.favoriteOptionsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_FavoriteOptions);
    this.plannerVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Planning);
    this.appointmentsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Appointments);
    this.companiesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Companies);
    this.languagesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Languages);
    this.currenciesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Currencies);
    this.countriesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Countries);
    this.tagsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Tags);
    this.alertsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Alerts);
    this.eventsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Events);
    this.translationsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Translations);
    this.customFieldsOrganizationsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_CustomFields_Organizations);
    this.customFieldsSuppliersVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_CustomFields_Organizations);
    this.customFieldsPeopleVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_CustomFields_People);
    this.customFieldsProductsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_CustomFields_Products);
    this.customFieldsBatchesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_CustomFields_Batches);
    this.printersVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Printers)
    this.scalesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Scales)
    this.devicesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Devices)
    this.paperSizeVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_PaperSizes)
    this.notificationsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Notifications)
    this.reportsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Reports);
    this.tenantsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_Tenants);
    this.stockClosures = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_StockClosures);
    this.rolesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_UserConfig_Roles);
    this.usersVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_UserConfig_Users);
    this.documentTypesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemComfig_Document_types);
    this.emailTemplatesVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_EmailTemplates);
    this.externalServiceNotificationsVisible = this.configService?.isNavBarOptionItemVisible(ENavBarOptions.NavBar_Admin_SystemConfig_ExternalServiceNotifications);
  }


  //#region  Active Appointments

  activeAppointments: number = 0;

  getActiveAppointments = async (): Promise<void> => {
    const params = {
      UserName: this.auth.userValue?.userName ?? '',
      FilterStateId: EAppointmentsFilterStates.Actives
    };
    const { total } = await firstValueFrom(this.api.resources.configuration.appointments.list({ params }));
    this.activeAppointments = total;
  }

  //#endregion

  //#region Active Alerts

  activeAlerts: Alert[] = [];

  getActiveAlerts = async (): Promise<void> => {
    const result = await firstValueFrom(this.api.resources.configuration.alerts.list({}));
    const alerts = result.data;

    this.activeAlerts = alerts.filter(alert => {
      const start = new Date(alert.startShowDateTime);
      const end = alert.endShowDateTime != null ? new Date(alert.endShowDateTime) : undefined;
      const now = new Date();
      return (start <= now && (!end || (end >= now)));
    });
  }

  //#endregion

  //#region WbSocket

  private hubConnectionConfiguration?:HubConnection;
  private hubConnectionOperations?: HubConnection;

  private wsConnect = () => {
    const configuration = `${environment.api.ocelot}/hubs/configuration`;
    const operation = `${environment.api.ocelot}/hubs/operations`;

    this.hubConnectionConfiguration = new HubConnectionBuilder().withUrl(configuration, {
      skipNegotiation: true,
      withCredentials: true,
      transport: HttpTransportType.WebSockets,
    }).build();

    this.hubConnectionOperations = new HubConnectionBuilder().withUrl(operation, {
      skipNegotiation: true,
      withCredentials: true,
      transport: HttpTransportType.WebSockets,
    }).build();

    this.hubConnectionConfiguration.on('ActiveAppointments', async(userName:string, number:number) => {

      if (userName === this.auth.userValue?.userName)
        this.activeAppointments = number;
    });

    this.hubConnectionConfiguration.on('ActiveAlerts', async(alerts:Alert[]) => {

      this.activeAlerts = alerts;
    });

    this.hubConnectionOperations.on('WorkerTimeTicket/' + this.auth.userValue?.workerId, async(data: string, message: any) => {

      const checkWorkerTimeTikets = this.api.resources.workerTimeTickets.contextualizeQueryParams({ workerId: (this.auth.userValue!.workerId || '-1').toString(), temporary: 'true' }).getAll()
      this.activeTimeTikets = (await firstValueFrom(checkWorkerTimeTikets)).filter(x => x.workerId?.toString() === this.auth.userValue!.workerId!.toString());
    });


    this.hubConnectionConfiguration.start();
    this.hubConnectionOperations.start();


  }

  private wsDisconnect = () => {
    this.hubConnectionConfiguration?.stop();
    this.hubConnectionOperations?.stop();
  }

  //#endregion
}
